/**
 * @fileoverview Move & zoom: one photograph of a share on its own screen, at
 * its frame's (or collage tile's) shape, dragged to move it and pinched or
 * slid to zoom it. Moved out of plant-list/components/dialogs/SharePlantDialog
 * (batch 18, A105); nothing in it changed.
 *
 * Where: the share studio (SharePlantDialog: a tap on the picture, the Crop
 * band and its crop mark).
 * Settings: SHARE_ZOOM_MAX (config/tunables/plantList).
 * Words (en/es): plants.share.phone.* (moveZoom, moveZoomHint, zoom, done, reset).
 *
 * @module MoveZoom
 */

import React from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SHARE_ZOOM_MAX } from '@/config/tunables/plantList';
import { refitFocus } from '@/shared/lib/photoFit';
import type { Focus } from './shareStudioTypes';

/**
 * Moving and zooming one photograph on a phone, on its own screen: the frame
 * (or collage tile) at its shape, the photograph filling it; drag to move it,
 * pinch or the slider to zoom (1 to SHARE_ZOOM_MAX times filling it). Done
 * keeps it (when it was changed; opened and left as it is, nothing is saved); Reset puts it back centred and unzoomed. A crop made in a frame of
 * another shape (focus.a) is refitted to this one once the photograph's size
 * is known: the same centre, the zoom lowered only as far as needed (B86).
 *
 * @param props.src - The photograph's address.
 * @param props.aspect - The width over the height of the slot the photograph fills in the chosen size and layout: the box's shape.
 * @param props.focus - Where it sits now (with the shape it was cropped in, if it was).
 * @param props.onDone - Given the new place when Done is tapped after the photograph was moved or zoomed.
 * @param props.onClose - Called when closed without keeping.
 */
export const MoveZoom: React.FC<{
  src: string;
  aspect: number;
  focus: Focus;
  onDone: (focus: Focus) => void;
  onClose: () => void;
}> = ({ src, aspect, focus, onDone, onClose }) => {
  const { reactTranslate: t } = useTranslation();
  const [place, setPlace] = React.useState<Focus>({ x: focus.x, y: focus.y, z: focus.z ?? 1 });
  const [natural, setNatural] = React.useState<{ w: number; h: number } | null>(null);
  /** Refits the crop to this box's shape once the photograph's size is known (B86); the gardener has not moved it yet. */
  React.useEffect(() => {
    if (natural) setPlace((p) => refitFocus({ ...p, a: focus.a }, { width: natural.w, height: natural.h }, aspect));
    // Once per photograph loaded; later moves are the gardener's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [natural]);
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  /** Fingers on the photograph: where each is, for a drag (one) or a pinch (two). */
  const pointers = React.useRef(new Map<number, { x: number; y: number }>());
  /** The pinch's start: the fingers' distance and the zoom then. */
  const pinch = React.useRef<{ d: number; z: number } | null>(null);
  /** The gardener has moved or zoomed it here: only then does Done keep a new crop (an untouched one stays as it was cropped, not as refitted). */
  const touched = React.useRef(false);
  const z = place.z ?? 1;
  /**
   * The box's own size, read from its layout (not getBoundingClientRect, which a dialog still
   * scaling in reports smaller) and read again when it changes, so the photograph fills it.
   */
  const [size, setSize] = React.useState<{ w: number; h: number } | null>(null);
  /** The box once it is on the page (the dialog mounts its content after the first render). */
  const [boxEl, setBoxEl] = React.useState<HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    const el = boxEl;
    if (!el) return;
    /** Reads the box's size into state. */
    const measure = () => setSize({ w: el.clientWidth || 1, h: el.clientHeight || 1 });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [boxEl]);
  const bw = size?.w ?? 1;
  const bh = size?.h ?? 1;
  const scale = natural ? Math.max(bw / natural.w, bh / natural.h) * z : 1;
  const iw = natural ? natural.w * scale : bw;
  const ih = natural ? natural.h * scale : bh;
  /** How far the photograph can travel inside the frame, each way. */
  const spanX = Math.max(1, iw - bw);
  const spanY = Math.max(1, ih - bh);
  /**
   * A place across or down the frame, kept between its edges.
   *
   * @param n - 0..1, or past either end.
   * @returns n, held to 0..1.
   */
  const clamp = (n: number) => Math.min(1, Math.max(0, n));
  /**
   * A finger or the mouse goes down on the photograph: followed, and a second finger starts a pinch.
   *
   * @param event - The pointer event.
   */
  const down = (event: React.PointerEvent<HTMLDivElement>) => {
    // A pointer that is already gone makes setPointerCapture throw; the gesture goes on without the capture.
    try { boxRef.current?.setPointerCapture(event.pointerId); } catch { /* pointer gone */ }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { d: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), z };
    }
  };
  /**
   * A finger or the mouse moves: two fingers zoom, one drags the photograph within the frame.
   *
   * @param event - The pointer event.
   */
  const move = (event: React.PointerEvent<HTMLDivElement>) => {
    const last = pointers.current.get(event.pointerId);
    if (!last) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const start = pinch.current;
    if (pointers.current.size >= 2 && start) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      // Worked out here, not inside the updater: React runs the updater later, after a lifted
      // finger has cleared pinch.current, and reading it then threw (the Sentry error of B10).
      const next = Math.min(SHARE_ZOOM_MAX, Math.max(1, start.z * (d / start.d)));
      if (Number.isFinite(next)) { touched.current = true; setPlace((p) => ({ ...p, z: next })); }
      return;
    }
    const dx = event.clientX - last.x;
    const dy = event.clientY - last.y;
    touched.current = true;
    setPlace((p) => ({ ...p, x: clamp(p.x - dx / spanX), y: clamp(p.y - dy / spanY) }));
  };
  /**
   * A finger or the mouse lifts: no longer followed, and fewer than two fingers end a pinch.
   *
   * @param event - The pointer event.
   */
  const up = (event: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };
  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent scrollBody={false} className="flex h-[100dvh] max-h-none flex-col gap-3 rounded-none border-0 p-4 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 sm:max-w-lg sm:rounded-lg">
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg">{t('plants.share.phone.moveZoom')}</DialogTitle>
          <DialogDescription>{t('plants.share.phone.moveZoomHint')}</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 items-center justify-center" style={{ containerType: 'size' }}>
          <div
            ref={(node) => { boxRef.current = node; setBoxEl(node); }}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            className="relative overflow-hidden rounded-md bg-muted shadow-md"
            style={{ aspectRatio: `${aspect}`, width: `min(100cqw, ${aspect} * 100cqh)`, touchAction: 'none' }}
          >
            <img
              src={src}
              alt=""
              draggable={false}
              onLoad={(event) => setNatural({ w: event.currentTarget.naturalWidth, h: event.currentTarget.naturalHeight })}
              className="absolute max-w-none select-none"
              style={{ width: iw, height: ih, left: (bw - iw) * place.x, top: (bh - ih) * place.y }}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-lg text-muted-foreground" aria-hidden="true">−</span>
          <input
            type="range"
            min={1}
            max={SHARE_ZOOM_MAX}
            step={0.01}
            value={z}
            onChange={(event) => { touched.current = true; setPlace((p) => ({ ...p, z: Number(event.target.value) })); }}
            aria-label={t('plants.share.phone.zoom')}
            className="h-11 flex-1 accent-primary"
          />
          <span className="text-lg text-muted-foreground" aria-hidden="true">+</span>
        </div>
        <Button className="h-12 text-base" onClick={() => (touched.current ? onDone(place) : onClose())}>{t('plants.share.phone.done')}</Button>
        <Button type="button" variant="link" size="lg" onClick={() => { touched.current = true; setPlace({ x: 0.5, y: 0.5, z: 1 }); }}>
          {t('plants.share.phone.reset')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
